import { z } from 'zod';

/*
 * What staff add when starting a run (Atlas v2 `NewRun`): the meeting
 * transcripts, all at once, and who in them is the client and who an auditor.
 * The rules are Atlas's (`atlas/ui.py`), so a start Atlas would refuse is
 * refused here first, with a message staff can act on.
 */

export const MAX_TRANSCRIPTS = 10;
export const MAX_TRANSCRIPT_CHARS = 400_000;
export const MAX_TOTAL_CHARS = 1_000_000;
export const SPEAKER_ROLES = ['client', 'auditor'] as const;
export type SpeakerRole = (typeof SPEAKER_ROLES)[number];

export const TranscriptSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(200)
    .refine((n) => /\.(md|txt)$/i.test(n), 'A transcript must be a .md or .txt file'),
  text: z
    .string()
    .max(MAX_TRANSCRIPT_CHARS, `A transcript can be at most ${MAX_TRANSCRIPT_CHARS} characters`)
    .refine((t) => t.trim() !== '', 'The transcript is empty'),
});
export type Transcript = z.infer<typeof TranscriptSchema>;

/** Atlas's check: the name appears somewhere in some transcript, ignoring case. */
export function nameAppearsIn(name: string, transcripts: readonly Pick<Transcript, 'text'>[]) {
  const n = name.trim().toLowerCase();
  return n !== '' && transcripts.some((t) => t.text.toLowerCase().includes(n));
}

export const StartRunSchema = z
  .object({
    transcripts: z
      .array(TranscriptSchema)
      .min(1, 'Add at least one transcript')
      .max(MAX_TRANSCRIPTS, `At most ${MAX_TRANSCRIPTS} transcripts`),
    /** Full name as it appears in the meeting → role. */
    roles: z.record(z.string().trim().min(1).max(200), z.enum(SPEAKER_ROLES)),
  })
  .superRefine((v, ctx) => {
    const names = v.transcripts.map((t) => t.name);
    if (new Set(names).size !== names.length)
      ctx.addIssue({
        code: 'custom',
        path: ['transcripts'],
        message: 'Two files have the same name',
      });
    if (v.transcripts.reduce((n, t) => n + t.text.length, 0) > MAX_TOTAL_CHARS)
      ctx.addIssue({
        code: 'custom',
        path: ['transcripts'],
        message: `Transcripts total more than ${MAX_TOTAL_CHARS} characters`,
      });
    if (!Object.values(v.roles).includes('client'))
      ctx.addIssue({
        code: 'custom',
        path: ['roles'],
        message: 'Mark at least one person as the client',
      });
    const absent = Object.keys(v.roles).filter((n) => !nameAppearsIn(n, v.transcripts));
    if (absent.length)
      // A typo here would silently turn the client's words into an auditor's.
      ctx.addIssue({
        code: 'custom',
        path: ['roles'],
        message: `Not found in any transcript: ${absent.join(', ')}`,
      });
  });
export type StartRun = z.infer<typeof StartRunSchema>;

/**
 * Speaker names as transcripts usually write them, `Name:` or `[00:01:02] Name:`
 * at a line start (the pattern Atlas's own UI uses), most frequent first.
 */
export function speakersIn(text: string): string[] {
  const counts = new Map<string, number>();
  for (const m of text.matchAll(/^(?:\[[^\]]*\]\s*)?([A-Z][\w .'-]{0,40}?):\s/gm)) {
    const name = (m[1] ?? '').trim();
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);
}
